import { CoreApi, SlidingDoorComponent, ProductType } from '@inter-ikea-storageone/range';
import { BestaOpeningFunctionComponent } from '@inter-ikea-storageone/range-besta';
import { ComponentIds, Entity, HierarchyUtils, ProductComponent } from '@inter-ikea-core/core';
import { getAllowedHandleTypes, getFrontsInScene } from '../utils/frontLayouts';
import { getRange } from 'utils/queryUtils';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';

export const hideTopPanelsMenu = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  if (!selectedEntity) {
    return true;
  }
  const parentComponent = selectedEntity.hasComponent(ComponentIds.PARENT_COMPONENT_ID);
  if (!parentComponent) {
    return true;
  }

  const frameList = HierarchyUtils.hierarchy(selectedEntity)
    .filter((c: Entity) => c.hasComponent(ComponentIds.FRAME_COMPONENT_ID))
    .filter(Boolean);

  if (frameList.length > 1) {
    return true;
  }

  return false;
};

export const hideIntegradeHandlesMessageMainMenu = (coreApi: CoreApi) => {
  const frontsInScene = coreApi.entities.getByComponents([
    ComponentIds.DOOR_COMPONENT_ID,
    ComponentIds.DRAWER_COMPONENT_ID,
    SlidingDoorComponent.id
  ]);
  if (frontsInScene.length === 0) {
    return true;
  }

  const frontsWithIntegratedHandles = frontsInScene.filter(
    front => getAllowedHandleTypes(front).length === 0
  );
  return frontsInScene.length !== frontsWithIntegratedHandles.length;
};

export const hideIntegradeHandlesMessageSelectedMenu = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  return selectedEntity ? getAllowedHandleTypes(selectedEntity).length > 0 : true;
};

export const isSelectedEntityDrawer = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return false;
  return selectedEntity.hasComponent(ComponentIds.DRAWER_COMPONENT_ID);
};

export const isSelectedEntityDoor = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return false;
  return selectedEntity.hasComponent(ComponentIds.DOOR_COMPONENT_ID);
};

export const noFrameSelected = (coreApi: CoreApi): boolean => {
  return !(
    coreApi.entities.findSelectedEntity()?.hasComponent(ComponentIds.FRAME_COMPONENT_ID) ||
    coreApi.entities.findSelectedEntity()?.hasComponent(CornerSolutionComponent.id)
  );
};

export const isSelectedEntityPushOpen = (coreApi: CoreApi): boolean => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity || isSelectedEntityDoor(coreApi)) return false;
  const drawerOpeningFunction = selectedEntity?.getComponent<BestaOpeningFunctionComponent>(
    BestaOpeningFunctionComponent.id
  )?.openingFunction;
  if (drawerOpeningFunction === ProductType.BESTA_PUSH_OPEN) {
    return true;
  }
  return false;
};

export const hideMixedOpeningTypeMainMenu = (coreApi: CoreApi) => {
  const frontsInScene = coreApi.entities.getByComponents([
    ComponentIds.DOOR_COMPONENT_ID,
    ComponentIds.DRAWER_COMPONENT_ID
  ]);

  return frontsInScene.length === 1
    ? true
    : frontsInScene.every(
        (val, _, arr) =>
          val.getComponent<BestaOpeningFunctionComponent>(BestaOpeningFunctionComponent.id)
            ?.openingFunction ===
          arr[0].getComponent<BestaOpeningFunctionComponent>(BestaOpeningFunctionComponent.id)
            ?.openingFunction
      );
};

export const isSelectedEntityProductType = (coreApi: CoreApi, productType: ProductType) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  return !!selectedEntity && coreApi.entities.isProductType(selectedEntity, productType);
};

export const isProductType = (
  coreApi: CoreApi,
  selectedEntityId: number,
  productType: ProductType
) => {
  const entity = coreApi.entities.find(selectedEntityId);
  return (entity && coreApi.entities.isProductType(entity, productType)) || false;
};

// Error to be displayed when user tries to add door/handle/interior etc before being adding any frames or sections
export const hideNoFramesInSceneErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  return framesInScene.length > 0;
};

// Error to be displayed when user tries to add handles before being they have added doors.
export const hideNoDoorsInSceneErrorMessage = (coreApi: CoreApi) => {
  const doorsInScene =
    getRange() === 'pax'
      ? coreApi.entities.getByComponents([ComponentIds.DOOR_COMPONENT_ID, SlidingDoorComponent.id])
      : getFrontsInScene(coreApi);
  return doorsInScene.length > 0 || !hideNoFramesInSceneErrorMessage(coreApi);
};

export const canSelectedEntityBeMirrored = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return false;

  const productComponent = selectedEntity.getComponent<ProductComponent>(
    ComponentIds.PRODUCT_COMPONENT_ID
  )?.productRef;

  if (productComponent?.includes('--default') || productComponent?.includes('--mirrored')) {
    return false;
  }
  return true;
};
